import type { CompanyChatRecipientRow } from '../../services/fetchCompanyChatRecipients';
import type { ChatRecipientOption } from './useChatRecipientDirectory';

export function companyRowToPersonOption(row: CompanyChatRecipientRow): ChatRecipientOption {
  return {
    value: `person:${row.userId}`,
    kind: 'person',
    id: row.userId,
    label: row.fullName ?? row.username ?? row.email ?? row.userId,
    subtitle: 'Person',
  };
}

/** Exclude the signed-in user from person recipient picks. */
export function excludeSelfPersonOptions(
  options: ChatRecipientOption[],
  currentUserId: string | undefined
): ChatRecipientOption[] {
  if (!currentUserId) return options;
  return options.filter((opt) => opt.kind !== 'person' || opt.id !== currentUserId);
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Strip accidental person: prefixes; chat person ids must be users.id UUIDs. */
export function normalizeChatPersonRecipientId(raw: string): string | null {
  let id = (raw || '').trim();
  while (id.toLowerCase().startsWith('person:')) {
    id = id.slice(7).trim();
  }
  if (!id || !UUID_RE.test(id)) return null;
  return id;
}

/**
 * Keep person suggestions that already resolve to a directory user.
 * Drop IntentML / company_employee resource tokens (would 500 on person-deliver).
 */
export function filterSuggestedRecipientsForChat(
  suggested: ChatRecipientOption[],
  personOptions: ChatRecipientOption[]
): ChatRecipientOption[] {
  const directoryIds = new Set(personOptions.map((opt) => opt.id));
  const directoryNormalized = new Set(
    personOptions
      .map((opt) => normalizeChatPersonRecipientId(opt.id))
      .filter((id): id is string => Boolean(id))
  );
  return suggested.filter((opt) => {
    if (opt.kind !== 'person') return true;
    if (directoryIds.has(opt.id)) return true;
    const id = normalizeChatPersonRecipientId(opt.id);
    return Boolean(id && directoryNormalized.has(id));
  });
}

export function mergeRecipientOptions(
  suggested: ChatRecipientOption[],
  personOptions: ChatRecipientOption[],
  agentOptions: ChatRecipientOption[]
): ChatRecipientOption[] {
  const merged = new Map<string, ChatRecipientOption>();
  for (const opt of filterSuggestedRecipientsForChat(suggested, personOptions)) {
    const personId = opt.kind === 'person' ? normalizeChatPersonRecipientId(opt.id) : null;
    if (opt.kind === 'person' && personId) {
      merged.set(`person:${personId}`, {
        ...opt,
        id: personId,
        value: `person:${personId}`,
      });
      continue;
    }
    merged.set(opt.value, opt);
  }
  for (const opt of personOptions) merged.set(opt.value, opt);
  for (const opt of agentOptions) merged.set(opt.value, opt);
  return Array.from(merged.values()).sort((a, b) => a.label.localeCompare(b.label));
}
